'use client';

/**
 * StatusDot Component
 *
 * A small colored dot indicator for status visualization.
 * Supports multiple colors, sizes, and optional pulse animation.
 */

import Box, { type BoxProps } from '@mui/material/Box';
import { keyframes, styled, useTheme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { baseColors } from '../../theme/palette';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export type StatusDotColor = 'success' | 'warning' | 'error' | 'info' | 'teal' | 'purple' | 'neutral';
export type StatusDotSize = 'small' | 'medium' | 'large';

export interface StatusDotProps extends Omit<BoxProps, 'color'> {
  /**
   * Status color
   * @default 'success'
   */
  color?: StatusDotColor;

  /**
   * Dot size
   * @default 'medium'
   */
  size?: StatusDotSize;

  /**
   * Enable pulse animation
   * @default false
   */
  pulse?: boolean;

  /**
   * Tooltip text
   */
  tooltip?: string;

  /**
   * Custom label to display next to the dot
   */
  label?: string;
}

// ============================================================================
// Animations
// ============================================================================

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 currentColor;
    opacity: 0.7;
  }
  70% {
    box-shadow: 0 0 0 6px currentColor;
    opacity: 0;
  }
  100% {
    box-shadow: 0 0 0 0 currentColor;
    opacity: 0;
  }
`;

// ============================================================================
// Color Mapping
// ============================================================================

const getStatusColor = (color: StatusDotColor, isDark: boolean): string => {
  const colors: Record<StatusDotColor, string> = {
    success: isDark ? baseColors.success[400] : baseColors.success[500],
    warning: isDark ? baseColors.warning[400] : baseColors.warning[500],
    error: isDark ? baseColors.error[400] : baseColors.error[500],
    info: isDark ? baseColors.info[400] : baseColors.info[500],
    teal: isDark ? baseColors.teal[400] : baseColors.teal[500],
    purple: isDark ? baseColors.primary[400] : baseColors.primary[500],
    neutral: isDark ? 'rgba(148, 163, 184, 0.6)' : 'rgba(100, 116, 139, 0.6)',
  };
  return colors[color];
};

const getSizeValue = (size: StatusDotSize): number => {
  const sizes: Record<StatusDotSize, number> = {
    small: 6,
    medium: 8,
    large: 10,
  };
  return sizes[size];
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledDotProps {
  $color: string;
  $size: number;
  $pulse: boolean;
}

const StyledDot = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledDotProps>(({ $color, $size, $pulse }) => ({
  display: 'inline-block',
  width: $size,
  height: $size,
  borderRadius: '50%',
  backgroundColor: $color,
  flexShrink: 0,
  position: 'relative',

  // Pulse animation
  ...($pulse && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      backgroundColor: $color,
      animation: `${pulseAnimation} 2s ease-in-out infinite`,
    },
  }),
}));

const StyledContainer = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});

const StyledLabel = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

// ============================================================================
// Component
// ============================================================================

export const StatusDot = forwardRef<HTMLSpanElement, StatusDotProps>(
  ({ color = 'success', size = 'medium', pulse = false, tooltip, label, ...props }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const statusColor = getStatusColor(color, isDark);
    const sizeValue = getSizeValue(size);

    const dot = <StyledDot ref={ref} $color={statusColor} $size={sizeValue} $pulse={pulse} />;

    const content = label ? (
      <StyledContainer {...props}>
        {dot}
        <StyledLabel>{label}</StyledLabel>
      </StyledContainer>
    ) : (
      <Box
        component="span"
        {...props}
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      >
        {dot}
      </Box>
    );

    if (tooltip) {
      return (
        <GlassTooltip title={tooltip} arrow>
          {content}
        </GlassTooltip>
      );
    }

    return content;
  },
);

StatusDot.displayName = 'StatusDot';

export default StatusDot;
